import { useAuthContext } from '@/auth/useAuthContext';
import { PetAssessmentPanel } from '@/components/PetAssessmentPanel';
/**
 * /customers/:customerId/pets/:petId — Pet Detail Page.
 *
 * Redesigned hero-first layout:
 *   - Hero: large pet name, species/breed badge, age, photo placeholder, status pill.
 *   - Two-column: left = health + physical info cards; right = vaccinations + recent bookings.
 *   - Vaccination status as visual pills (green = current, red = expired, amber = expiring ≤30d).
 *   - Recent bookings as a compact timeline.
 *
 * Design system: warm-paper bg, OKLCH tokens, .num on all digits.
 */
import { PetFeedingPlanPanel } from '@/components/PetFeedingPlanPanel';
import { PetRegisterPanel } from '@/components/compliance/PetRegisterPanel';
import {
  type PetVaccination,
  type Vaccine,
  getCustomer,
  getCustomerBookings,
  listPetVaccinations,
  listPetsByCustomer,
  listVaccines,
} from '@/lib/api';
import type { CustomerBookingRow, Pet } from '@/lib/types';
import {
  AlertTriangle,
  ArrowLeft,
  Calendar,
  CheckCircle2,
  Clock,
  Edit,
  Heart,
  PawPrint,
  Scale,
  ShieldCheck,
  Syringe,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

// ── Helpers ─────────────────────────────────────────────────────────────────

function ageFromDob(dateOfBirth: string | null | undefined): string | null {
  if (!dateOfBirth) return null;
  const dob = new Date(dateOfBirth);
  if (Number.isNaN(dob.getTime())) return null;
  const years = Math.floor((Date.now() - dob.getTime()) / (365.25 * 24 * 60 * 60 * 1000));
  if (years < 1) {
    const months = Math.floor((Date.now() - dob.getTime()) / (30.5 * 24 * 60 * 60 * 1000));
    return `${months}mo`;
  }
  return `${years}y`;
}

function formatDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

type VaccineStatus = 'current' | 'expiring' | 'expired' | 'unknown';

function vaccineStatus(expiresOn: string | null): VaccineStatus {
  if (!expiresOn) return 'unknown';
  const exp = new Date(expiresOn);
  if (Number.isNaN(exp.getTime())) return 'unknown';
  const now = Date.now();
  const diff = exp.getTime() - now;
  if (diff < 0) return 'expired';
  if (diff < 30 * 24 * 60 * 60 * 1000) return 'expiring';
  return 'current';
}

function VaccPill({ status, name }: { status: VaccineStatus; name: string }) {
  const cls = {
    current: 'bg-success/10 text-success ring-success/25',
    expiring: 'bg-warning/15 text-warning-foreground ring-warning/30',
    expired: 'bg-destructive/10 text-destructive ring-destructive/25',
    unknown: 'bg-secondary text-muted-foreground ring-border',
  }[status];
  const Icon = {
    current: CheckCircle2,
    expiring: Clock,
    expired: AlertTriangle,
    unknown: Syringe,
  }[status];
  return (
    <span
      className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium ring-1 ring-inset ${cls}`}
    >
      <Icon className="size-3" strokeWidth={2.5} />
      {name}
    </span>
  );
}

function InfoRow({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <div className="flex items-baseline justify-between gap-3 py-2">
      <span className="text-[11px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
        {label}
      </span>
      <span className="text-right text-sm text-foreground">{value}</span>
    </div>
  );
}

function Card({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`elevate rounded-xl bg-surface ring-1 ring-border ${className}`}>
      {children}
    </div>
  );
}

function CardHeader({ icon: Icon, title }: { icon: React.ElementType; title: string }) {
  return (
    <div className="flex items-center gap-2 border-b border-border px-5 py-3.5">
      <Icon className="size-4 text-muted-foreground" strokeWidth={1.75} />
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
    </div>
  );
}

// ── Pet Detail Page ──────────────────────────────────────────────────────────

export function PetDetailPage() {
  const { customerId, petId } = useParams<{ customerId: string; petId: string }>();
  const { state: authState } = useAuthContext();
  const capabilities = authState.me?.capabilities ?? [];
  // Migration 0100 — the Schedule-4 register panel is gated by
  // compliance.records (the API route rejects everyone else).
  const canViewRegister = capabilities.includes('*') || capabilities.includes('compliance.records');

  const [pet, setPet] = useState<Pet | null>(null);
  const [bookings, setBookings] = useState<CustomerBookingRow[]>([]);
  const [vaccinations, setVaccinations] = useState<PetVaccination[]>([]);
  const [vaccines, setVaccines] = useState<Vaccine[]>([]);
  const [customerName, setCustomerName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!customerId || !petId) return;
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [petsRes, bookingsRes, vacsRes, vaccinesRes, customerRes] = await Promise.all([
          listPetsByCustomer(customerId!),
          getCustomerBookings(customerId!).catch(() => [] as CustomerBookingRow[]),
          listPetVaccinations(petId!).catch(() => ({ items: [] })),
          listVaccines(false).catch(() => ({ vaccines: [] })),
          getCustomer(customerId!).catch(() => null),
        ]);
        if (cancelled) return;
        const found = petsRes.find((p) => p.id === petId);
        if (!found) {
          setError('Pet not found');
          return;
        }
        setPet(found);
        setBookings(bookingsRes.filter((b: CustomerBookingRow) => b.booking.petId === petId));
        setVaccinations(vacsRes.items);
        setVaccines(vaccinesRes.vaccines);
        if (customerRes) {
          setCustomerName(`${customerRes.firstName} ${customerRes.lastName}`);
        }
      } catch (e) {
        if (!cancelled) setError((e as Error).message ?? 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [customerId, petId]);

  if (loading) {
    return (
      <div className="mx-auto max-w-[1400px] px-6 py-10 md:px-10">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-40 rounded bg-secondary" />
          <div className="h-36 rounded-2xl bg-secondary" />
          <div className="grid gap-4 lg:grid-cols-[3fr_2fr]">
            <div className="space-y-4">
              <div className="h-48 rounded-xl bg-secondary" />
              <div className="h-48 rounded-xl bg-secondary/60" />
            </div>
            <div className="h-64 rounded-xl bg-secondary" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !pet) {
    return (
      <div className="mx-auto max-w-[1400px] px-6 py-10 md:px-10">
        <Link
          to={`/customers/${customerId}`}
          className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground press"
        >
          <ArrowLeft className="size-4" strokeWidth={1.75} />
          Back to customer
        </Link>
        <div className="rounded-xl bg-destructive/10 px-5 py-4 text-sm text-destructive ring-1 ring-destructive/20">
          {error ?? 'Pet not found'}
        </div>
      </div>
    );
  }

  const age = ageFromDob(pet.dateOfBirth);
  const vaccineById = new Map(vaccines.map((v) => [v.id, v]));

  // Recent bookings — last 10, most recent first.
  const recentBookings = [...bookings]
    .sort((a, b) => b.booking.createdAt.localeCompare(a.booking.createdAt))
    .slice(0, 10);

  // Parse structured fields.
  const allergies: string[] = (() => {
    try {
      const arr = JSON.parse(pet.allergiesJson ?? '[]');
      return Array.isArray(arr)
        ? arr
            .map((a: { name?: string } | string) => (typeof a === 'string' ? a : (a.name ?? '')))
            .filter(Boolean)
        : [];
    } catch {
      return [];
    }
  })();

  const medications: Array<{ name: string; dose?: string; frequency?: string }> = (() => {
    try {
      const arr = JSON.parse(pet.medicationsJson ?? '[]');
      return Array.isArray(arr) ? arr : [];
    } catch {
      return [];
    }
  })();

  // Insurance status
  const insuranceExpired =
    pet.insuranceExpiresOn && new Date(pet.insuranceExpiresOn).getTime() < Date.now();

  return (
    <div className="mx-auto max-w-[1400px] px-6 py-10 md:px-10">
      {/* ── Back link ───────────────────────────────────────────────── */}
      <Link
        to={`/customers/${customerId}`}
        className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground press"
      >
        <ArrowLeft className="size-4" strokeWidth={1.75} />
        {customerName || 'Customer'}
      </Link>

      {/* ── Hero ────────────────────────────────────────────────────── */}
      <div className="elevate mb-8 overflow-hidden rounded-2xl bg-surface ring-1 ring-border">
        <div className="flex flex-col gap-5 px-6 py-6 sm:flex-row sm:items-start sm:gap-7 sm:px-8 sm:py-8">
          {/* Photo placeholder */}
          <div className="flex size-24 shrink-0 items-center justify-center rounded-2xl bg-secondary ring-1 ring-border sm:size-28">
            <PawPrint className="size-10 text-muted-foreground/50" strokeWidth={1.5} />
          </div>

          {/* Main info */}
          <div className="flex-1 min-w-0">
            {/* Eyebrow */}
            <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-muted-foreground">
              Pet record
            </p>

            {/* Name */}
            <h1 className="mt-1 text-3xl font-medium tracking-tight text-foreground sm:text-4xl">
              {pet.name}
            </h1>

            {/* Meta badges */}
            <div className="mt-2.5 flex flex-wrap items-center gap-2">
              {/* Species/breed badge */}
              <span className="rounded-full bg-secondary px-3 py-1 text-xs font-medium text-foreground ring-1 ring-border">
                {pet.species[0]?.toUpperCase()}
                {pet.species.slice(1)}
                {pet.breed ? ` · ${pet.breed}` : ''}
              </span>
              {/* Age */}
              {age && (
                <span className="rounded-full bg-secondary px-3 py-1 text-xs font-medium text-muted-foreground ring-1 ring-border">
                  <span className="num">{age}</span> old
                </span>
              )}
              {/* Size */}
              {pet.sizeCategory && (
                <span className="rounded-full bg-secondary px-3 py-1 text-xs font-medium text-muted-foreground ring-1 ring-border">
                  {pet.sizeCategory}
                </span>
              )}
              {/* Sex/neutered */}
              {pet.sex && (
                <span className="rounded-full bg-secondary px-3 py-1 text-xs font-medium text-muted-foreground ring-1 ring-border">
                  {pet.sex}
                  {pet.neutered === 1 ? ' · neutered' : pet.neutered === 0 ? ' · intact' : ''}
                </span>
              )}
            </div>

            {/* Medical alert strip */}
            {pet.medicalConditions && (
              <div className="mt-3 flex items-start gap-2 rounded-lg bg-warning/10 px-3 py-2 ring-1 ring-warning/25">
                <AlertTriangle
                  className="mt-0.5 size-3.5 shrink-0 text-warning-foreground"
                  strokeWidth={2}
                />
                <p className="text-xs text-warning-foreground">{pet.medicalConditions}</p>
              </div>
            )}
          </div>

          {/* Edit CTA */}
          <div className="shrink-0">
            <Link
              to={`/customers/${customerId}/pets/${petId}/edit`}
              className="press flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white hover:bg-primary/90"
            >
              <Edit className="size-4" strokeWidth={2} />
              Edit
            </Link>
          </div>
        </div>
      </div>

      {/* ── Two-column body ──────────────────────────────────────────── */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[3fr_2fr]">
        {/* ===== LEFT COLUMN ===== */}
        <div className="space-y-5">
          {/* Health & Care */}
          <Card>
            <CardHeader icon={Heart} title="Health & Care" />
            <div className="divide-y divide-border px-5">
              {pet.weightKg != null && (
                <div className="flex items-center gap-2 py-2.5">
                  <Scale className="size-3.5 shrink-0 text-muted-foreground" strokeWidth={1.75} />
                  <span className="text-[11px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
                    Weight
                  </span>
                  <span className="ml-auto num text-sm font-medium">{pet.weightKg} kg</span>
                </div>
              )}
              {pet.color && <InfoRow label="Colour" value={pet.color} />}
              {allergies.length > 0 && (
                <div className="py-2.5">
                  <p className="mb-1.5 text-[11px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
                    Allergies
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {allergies.map((a) => (
                      <span
                        key={a}
                        className="rounded-full bg-destructive/10 px-2.5 py-0.5 text-[11px] font-medium text-destructive ring-1 ring-inset ring-destructive/25"
                      >
                        {a}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {medications.length > 0 && (
                <div className="py-2.5">
                  <p className="mb-1.5 text-[11px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
                    Medications
                  </p>
                  <div className="space-y-1">
                    {medications.map((m, i) => (
                      // biome-ignore lint/suspicious/noArrayIndexKey: medication list has no stable key
                      <div key={i} className="flex items-start gap-2 text-sm">
                        <span className="mt-1 size-1.5 shrink-0 rounded-full bg-primary/40" />
                        <span>
                          <span className="font-medium">{m.name}</span>
                          {m.dose && <span className="text-muted-foreground"> · {m.dose}</span>}
                          {m.frequency && (
                            <span className="text-muted-foreground"> · {m.frequency}</span>
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {pet.feedingInstructions && (
                <div className="py-2.5">
                  <p className="mb-1 text-[11px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
                    Feeding
                  </p>
                  <p className="text-sm leading-relaxed text-foreground">
                    {pet.feedingInstructions}
                  </p>
                </div>
              )}
              {pet.behaviorNotes && (
                <div className="py-2.5">
                  <p className="mb-1 text-[11px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
                    Behaviour
                  </p>
                  <p className="text-sm leading-relaxed text-foreground">{pet.behaviorNotes}</p>
                </div>
              )}
              {pet.specialInstructions && (
                <div className="py-2.5">
                  <p className="mb-1 text-[11px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
                    Special instructions
                  </p>
                  <p className="text-sm leading-relaxed text-foreground">
                    {pet.specialInstructions}
                  </p>
                </div>
              )}
              {!pet.weightKg &&
                !pet.color &&
                !allergies.length &&
                !medications.length &&
                !pet.feedingInstructions &&
                !pet.behaviorNotes &&
                !pet.specialInstructions && (
                  <p className="py-5 text-center text-sm text-muted-foreground">
                    No health data recorded.
                  </p>
                )}
            </div>
          </Card>

          {/* Structured feeding plan (migration 0101) */}
          {petId && (
            <PetFeedingPlanPanel petId={petId} legacyText={pet.feedingInstructions ?? null} />
          )}

          {/* Physical / Identity */}
          <Card>
            <CardHeader icon={ShieldCheck} title="Identity" />
            <div className="divide-y divide-border px-5">
              <InfoRow label="Microchip" value={pet.microchip ?? null} />
              <InfoRow label="Registered with" value={pet.microchipRegisteredWith ?? null} />
              {pet.microchipRegistrationDate && (
                <InfoRow
                  label="Registration date"
                  value={formatDate(pet.microchipRegistrationDate)}
                />
              )}
              {pet.dateOfBirth && (
                <InfoRow label="Date of birth" value={formatDate(pet.dateOfBirth)} />
              )}
              {/* Insurance */}
              {pet.insuranceProvider && (
                <div className="flex items-center gap-2 py-2.5">
                  <span className="text-[11px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
                    Insurance
                  </span>
                  <span className="ml-auto text-right text-sm">
                    {pet.insuranceProvider}
                    {pet.insurancePolicyNumber && (
                      <span className="num ml-1 text-muted-foreground">
                        #{pet.insurancePolicyNumber}
                      </span>
                    )}
                  </span>
                  {pet.insuranceExpiresOn && (
                    <span
                      className={`rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${
                        insuranceExpired
                          ? 'bg-destructive/10 text-destructive ring-destructive/25'
                          : 'bg-success/10 text-success ring-success/25'
                      }`}
                    >
                      {insuranceExpired ? 'Expired' : `Exp ${formatDate(pet.insuranceExpiresOn)}`}
                    </span>
                  )}
                </div>
              )}
              {!pet.microchip && !pet.insuranceProvider && !pet.dateOfBirth && (
                <p className="py-5 text-center text-sm text-muted-foreground">
                  No identity data recorded.
                </p>
              )}
            </div>
          </Card>

          {/* Notes */}
          {pet.notes && (
            <Card>
              <CardHeader icon={PawPrint} title="Internal notes" />
              <p className="px-5 py-4 text-sm leading-relaxed text-foreground">{pet.notes}</p>
            </Card>
          )}
        </div>

        {/* ===== RIGHT COLUMN ===== */}
        <div className="space-y-5">
          {/* Evaluation day (migration 0099) */}
          {petId && <PetAssessmentPanel petId={petId} />}

          {/* Vaccinations */}
          <Card>
            <CardHeader icon={Syringe} title="Vaccinations" />
            <div className="px-5 py-4">
              {vaccinations.length === 0 ? (
                <p className="text-sm text-muted-foreground">No vaccinations recorded.</p>
              ) : (
                <div className="space-y-3">
                  {vaccinations.map((v) => {
                    const vaccineName = vaccineById.get(v.vaccineId)?.name ?? v.vaccineId;
                    const status = vaccineStatus(v.expiresOn);
                    return (
                      <div key={v.id} className="flex items-start justify-between gap-3">
                        <div>
                          <VaccPill status={status} name={vaccineName} />
                          {v.expiresOn && (
                            <p className="mt-1 num text-[11px] text-muted-foreground">
                              {status === 'expired' ? 'Expired' : 'Expires'}{' '}
                              {formatDate(v.expiresOn)}
                            </p>
                          )}
                          {v.administeredOn && (
                            <p className="num text-[11px] text-muted-foreground">
                              Given {formatDate(v.administeredOn)}
                            </p>
                          )}
                        </div>
                        {v.verifiedAt && (
                          <span className="flex items-center gap-1 rounded-full bg-success/10 px-2 py-0.5 text-[10px] font-medium text-success ring-1 ring-inset ring-success/25">
                            <CheckCircle2 className="size-3" strokeWidth={2.5} />
                            Verified
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </Card>

          {/* Recent bookings */}
          <Card>
            <CardHeader icon={Calendar} title="Recent bookings" />
            <div className="divide-y divide-border">
              {recentBookings.length === 0 ? (
                <p className="px-5 py-5 text-sm text-muted-foreground">No bookings yet.</p>
              ) : (
                recentBookings.map((row) => {
                  const bk = row.booking;
                  const statusColors: Record<string, string> = {
                    checked_in: 'bg-primary/10 text-primary ring-primary/25',
                    checked_out: 'bg-success/10 text-success ring-success/25',
                    confirmed: 'bg-secondary text-muted-foreground ring-border',
                    cancelled: 'bg-destructive/10 text-destructive ring-destructive/25',
                    no_show: 'bg-destructive/10 text-destructive ring-destructive/25',
                    pending: 'bg-warning/15 text-warning-foreground ring-warning/30',
                  };
                  const statusCls =
                    statusColors[bk.status] ?? 'bg-secondary text-muted-foreground ring-border';
                  return (
                    <Link
                      key={bk.id}
                      to={`/bookings/${bk.id}`}
                      className="press flex items-start justify-between gap-3 px-5 py-3 hover:bg-secondary/30"
                    >
                      <div>
                        <p className="text-sm font-medium capitalize text-foreground">Booking</p>
                        <p className="num mt-0.5 text-[11px] text-muted-foreground">
                          {formatDate(bk.createdAt)}
                        </p>
                      </div>
                      <span
                        className={`mt-0.5 rounded-full px-2.5 py-0.5 text-[10px] font-medium ring-1 ring-inset ${statusCls}`}
                      >
                        {bk.status.replace('_', ' ')}
                      </span>
                    </Link>
                  );
                })
              )}
            </div>
          </Card>
        </div>
      </div>

      {/* Migration 0100 — licensing register (Schedule-4 field set + gaps) */}
      {canViewRegister && petId && (
        <div className="mt-6">
          <PetRegisterPanel petId={petId} />
        </div>
      )}
    </div>
  );
}
